import React, { useState, useRef, useEffect, Ref } from 'react';
import { useReactToPrint } from 'react-to-print';
import { httpGet } from '../../../modules/Backend';
import { getCurrentFormattedDate, displayDateTime } from '../../../helpers/DateHelper';
import LoadingButton from '../LoadingButton';
import { formatAddressParts } from '../../../helpers/StringHelper';
import { Intent } from '../recallWizard/recallData';
import { EntityNames } from '../../../enums/EntityNames';

interface PrintableComponentProps {
  data: any;
  ref: React.RefObject<HTMLDivElement>
}

// Printable component with a ref to allow printing
const PrintableComponent = React.forwardRef<HTMLDivElement, PrintableComponentProps>(({ data }, ref) => (

  <div ref={ref}>
    {data && <div className='print-pick-list__wrapper'>
      <div className='print-pick-list__header'>
        <p>{getCurrentFormattedDate()}</p>
      </div>
      <div className='print-pick-list__information'>
        <h1>Recall</h1>
        <h1>{data.accountName}</h1>
        <h1>{data.accountReference}</h1>
        <div>
          <h2>Our Ref: {data.reference}</h2>
          <h2>Client Ref: {data.orderNumber}</h2>
        </div>
        <h2>Added On: {data.addedOn ? displayDateTime(data.addedOn) : '--'}</h2>
        <h2>
          Requested By: {data.requestedBy}
          {data.onBehalfOf && data.onBehalfOf !== data.requestedBy && ` | On Behalf Of: ${data.onBehalfOf}`}
        </h2>
        <h2>Contact Number: {data.contactNumber ?? data.ContactNumber ?? '--'}</h2>
        <p className='p--bold'>Invoice Address</p>
        <p>{formatAddressParts(data.invoiceAddress).join(", ")}</p>
        <p className='p--bold'>Deliver to</p>
        <p>{formatAddressParts(data.deliveryAddress).join(", ")}</p>
        <p className='p--bold'>Delivery method</p>
        <p className=''>{data.deliveryMethod}</p>
      </div>
      {data && data.items.map((item: any, i: number) => (
        <>
          <div className='print-pick-list__item-wrapper'>
            <div className='print-pack-list__item-information-wrapper'>
              {(item.intent === Intent.PartCase || item.intent === Intent.WholeCase) &&
                <div className='print-pick-list__pairs-container'>
                  <div className='grid-x print-pick-list__compact-row print-pick-list__left-column'>
                    <p className='small-2'>Case</p>
                    <p className='small-10'>{item.caseReference}</p>
                  </div>
                  <div className='grid-x print-pick-list__compact-row print-pick-list__right-column'>
                    <p className='small-2'>Specimen</p>
                    <p className='small-10'>{item.specimenReference}</p>
                  </div>
                  <div className='grid-x print-pick-list__compact-row print-pick-list__left-column'>
                    <p className='small-2'>Year</p>
                    <p className='small-10'>{item.year}</p>
                  </div>
                  {item.rangeNotes && (
                    <div className='grid-x print-pick-list__compact-row print-pick-list__right-column'>
                      <p className='small-2'>Range Notes</p>
                      <p className='small-10'>{item.rangeNotes}</p>
                    </div>
                  )}
                  <div className='grid-x print-pick-list__compact-row print-pick-list__left-column'>
                    <p className='small-2'>Range</p>
                    <p className='small-10'>{item.range}</p>
                  </div>
                  {item.requestorComments && (
                    <div className='grid-x print-pick-list__compact-row print-pick-list__right-column'>
                      <p className='small-2'>Customer Comments</p>
                      <p className='small-10'>{item.requestorComments}</p>
                    </div>
                  )}
                  <div className='grid-x print-pick-list__compact-row print-pick-list__left-column'>
                    <p className='small-2'>Type</p>
                    <p className='small-10'>{item.type}</p>
                  </div>
                  {item.itemNotes && (
                    <div className='grid-x print-pick-list__compact-row print-pick-list__right-column'>
                      <p className='small-2'>CellNass Notes</p>
                      <p className='small-10'>{item.itemNotes}</p>
                    </div>
                  )}
                  <div className='grid-x print-pick-list__compact-row print-pick-list__left-column'>
                    <p className='small-2'>Quantity</p>
                    <p className='small-10'>{item.quantity}</p>
                  </div>
                </div>
              }
              {item.intent === Intent.WholeBox &&
                <>
                  <div className='grid-x'>
                    <p className='small-3'>Whole Box</p>
                    <p className='small-3'>{item.boxReference}</p>
                  </div>
                  <div className='grid-x'>
                    <p className='small-3'>Sequence number</p>
                    <p className='small-3'>{item.boxSequence}</p>
                  </div>
                  <div className='grid-x'>
                    <p className='small-3'>Volume</p>
                    <p className='small-3'>{item.boxVolume}</p>
                  </div>
                </>
              }
            </div>
          </div>
        </>
      ))
      }
    </div>}
  </div >
)
);

interface PrinterProps {
  recallId: string
}

function PrintRecallPackList({ recallId }: PrinterProps) {
  const [data, setData] = useState<any[]>();
  const [shouldPrint, setShouldPrint] = useState(false);
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    const response = await httpGet(`api/${EntityNames.recalls}/packPrintData?recallId=${recallId}`);
    if (response.data) {
      setData(response.data);
      setShouldPrint(true);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => { setShouldPrint(false); setLoading(false) }
  });

  useEffect(() => {
    if (shouldPrint && componentRef.current) {
      handlePrint();
    }
  }, [shouldPrint]);

  const handleFetchAndPrint = async () => {
    if (loading)
      return
    setLoading(true)
    fetchData()
  };

  return (
    <div>
      <LoadingButton className="button button--primary button--with-icon button--small"
        onPress={handleFetchAndPrint}
        isDisabled={loading}
        isLoading={loading}
        loadingText="Preparing"
        buttonText="Print packing sheet">
        <></>
      </LoadingButton>
      <div style={{ display: 'none' }}>
        {data && <PrintableComponent ref={componentRef} data={data} />}
      </div>
    </div>
  );
};

export default PrintRecallPackList;